
import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6chcdaohb161tfd837g';

export default function RequestForm() {
  const [formData, setFormData] = useState({ city: '', zip: '', email: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.city.trim() || !formData.zip.trim() || !formData.email.trim()) return;

    setStatus('submitting');
    try {
      const body = new URLSearchParams();
      body.append('city', formData.city);
      body.append('zip', formData.zip);
      body.append('email', formData.email);

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({ city: '', zip: '', email: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-brand-navy to-brand-navy-dark relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-red/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-brand-gold/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>

      <div className="relative z-10 max-w-[800px] mx-auto px-6">
        <div className="text-center mb-10">
          <span className="inline-block text-brand-gold font-semibold text-sm uppercase tracking-widest mb-3">
            Not In Our Area Yet?
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Request Service In Your City
          </h2>
          <p className="text-white/70 max-w-lg mx-auto">
            We&apos;re constantly expanding our coverage. Let us know where you&apos;d like to see us next and we&apos;ll notify you when we arrive.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-green-600 text-3xl w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl font-bold text-brand-navy mb-2">Request Submitted</h3>
              <p className="text-gray-600">Thank you! We&apos;ll notify you when service becomes available in your area.</p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-6 text-brand-red font-semibold hover:text-brand-red-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form
              id="service-area-inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
            >
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1.5">City Name</label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    placeholder="Enter your city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1.5">ZIP Code</label>
                  <input
                    id="zip"
                    name="zip"
                    type="text"
                    placeholder="Enter ZIP code"
                    value={formData.zip}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red transition-colors"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-brand-red text-white py-4 rounded-lg font-semibold text-base hover:bg-brand-red-dark transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'submitting' ? (
                  'Submitting...'
                ) : (
                  <>
                    Request Coverage
                    <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
                  </>
                )}
              </button>
              {status === 'error' && (
                <p className="text-red-500 text-sm text-center mt-3">Something went wrong. Please try again.</p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
